<script setup lang="ts">
import {
  NamedBindings,
  TranslationService,
  getContainer,
} from '@dandelion/core';
import { Ref, ref, watch } from 'vue';
import RecursiveReadmissionHistory from '../components/RecursiveReadmissionHistory.vue';
import { Messages } from '../enum/Messages';
import { NamedServices } from '../enum/NamedServices';
import { ReadmissionHistory } from '../services/ReadmissionManagerRunner';
import { ReadmissionService } from '../services/ReadmissionService';

const ts = getContainer().get(
  NamedBindings.TranslationService
) as TranslationService;
const readmissionService = getContainer().get(
  NamedServices.ReadmissionService
) as ReadmissionService;

let showUser: Ref<boolean> = readmissionService.showUserDialog;
let selectedReadmission: Ref<ReadmissionHistory | undefined> = ref(undefined);
let patientHistory: Ref<Array<ReadmissionHistory>> =
  readmissionService.patientHistory;
let isSelecting = ref(true);
let selectedReadmissionIndex = ref(-1);
let selectButtonsLabel: Array<string> = [];

watch(showUser, () => {
  const userDialog = document.getElementById(
    'readmission-user-dialog'
  ) as HTMLDialogElement;
  showUser.value ? userDialog.showModal() : userDialog.close();
  selectButtonsLabel = patientHistory.value.map(() => 'Select this history');
});

function setHistory(history: ReadmissionHistory, index: number) {
  isSelecting.value = false;
  selectedReadmissionIndex.value = index;
  selectedReadmission.value = history;
  selectButtonsLabel = patientHistory.value.map(
    (_history: ReadmissionHistory, i: number) => {
      return i === index ? 'Selected' : 'Select this history';
    }
  );
}

async function isSamePatient() {
  if (!readmissionService.currentRunner) return;
  await readmissionService.currentRunner.updateAdmissionsChain(
    selectedReadmission.value!
  );
}

async function isNotSamePatient() {
  if (!readmissionService.currentRunner) return;
  await readmissionService.currentRunner.saveHashForNotReadmission();
}

async function closeDialog() {
  readmissionService.dialogResult.value = false;
  showUser.value = false;
}

async function confirmReadmission() {
  readmissionService.dialogResult.value = true;
  isSamePatient();
}

async function notReadmission() {
  readmissionService.dialogResult.value = true;
  isNotSamePatient();
}
</script>

<template>
  <dialog id="readmission-user-dialog">
    <section id="readmission-header">
      <h2 v-html="ts.t(Messages.IS_THE_SAME_PATIENT)"></h2>
      <ion-icon
        name="close-outline"
        id="close-readmission-dialog"
        @click="closeDialog()"
      ></ion-icon>
    </section>
    <h4 v-html="ts.t('Previous ICU admissions')"></h4>
    <span class="error" v-if="patientHistory.length > 1" v-html="ts.t('We found more than one compatible patient')"></span>
    <table class="case-list-table">
      <thead>
        <tr>
          <th v-html="ts.t('Admission Date')"></th>
          <th v-html="ts.t('Case Id')"></th>
        </tr>
      </thead>
      <tbody
        :class="{ selected: selectedReadmissionIndex === index }"
        v-for="(readmissionHistory, index) in patientHistory"
        :key="index"
      >
        <tr>
          <td colspan="3">
            <div class="readmission-header">
              <span
                class="readmission-title"
                v-html="ts.t('Patient') + ': ' + (index + 1)"
              ></span>
              <button
                class="select-history-btn"
                @click="setHistory(readmissionHistory, index)"
                v-html="ts.t(selectButtonsLabel[index])"
              ></button>
            </div>
          </td>
        </tr>
        <RecursiveReadmissionHistory
          :prevAdmission="readmissionHistory"
        ></RecursiveReadmissionHistory>
      </tbody>
    </table>
    <button
      class="primary"
      :disabled="isSelecting"
      @click="confirmReadmission()"
      v-html="ts.t('Confirm')"
    ></button>
    <button
      class="secondary"
      @click="notReadmission()"
      v-html="ts.t('This isn\'t a readmission')"
    ></button>
  </dialog>
</template>

<style>
#readmission-user-dialog {
  border-radius: 1rem;
}

#readmission-user-dialog #readmission-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  height: 2rem;
  font-size: 2rem;
}

#readmission-user-dialog #readmission-header h2 {
  height: 2rem;
  font-size: 1.5rem;
  text-align: center;
}

#readmission-user-dialog #readmission-header ion-icon {
  color: grey;
  margin-right: 1rem;
  height: 2rem;
  font-size: 2rem;
}

#readmission-user-dialog #readmission-header ion-icon:hover {
  background-color: var(--secondary-color);
  color: white;
  border-radius: 0.5rem;
}

#readmission-user-dialog table {
  margin: 0 0 16px 0;
  border-spacing: 0;
}

#readmission-user-dialog .case-list-table {
  max-height: 66vh;
  overflow: auto;
  margin: 1.5rem 0;
}

#readmission-user-dialog thead th {
  font-size: 1.2rem;
  padding: 0.8rem;
}

#readmission-user-dialog thead th:first-of-type {
  border-right: 2px solid #fff;
}

#readmission-user-dialog tbody.selected td {
  background-color: rgba(0, 128, 0, 0.74) !important;
  color: white;
}

#readmission-user-dialog .readmission-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.2rem 0.5rem;
}

#readmission-user-dialog .readmission-title {
  font-size: 1.1rem;
  font-weight: 700;
}

#readmission-user-dialog tbody > tr:first-of-type > td {
  padding: 0;
}

#readmission-user-dialog tbody tr:not(:first-of-type) td {
  padding: 0.4rem 0.5rem;
}

#readmission-user-dialog tbody:not(:first-of-type) .readmission-header {
  border-top: 1px solid #000;
}

#readmission-user-dialog .readmission-header button {
  height: auto;
  padding: 8px;
  margin: 6px 0;
}

#readmission-user-dialog .primary {
  margin-right: 8px;
}

#readmission-user-dialog .primary:disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
